import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';

import { styles } from './styles';
import {
  CheckMarkCell,
  InfoRow,
  WrappingInfoRow,
  TableRow,
  CompactTableCell,
  PDFLogo,
  ProductImage,
  PDFBarcode,
} from './PDFComponents';
import { getBorderColor, formatDateUTC } from './utils';
import { getImageUrlForPDF } from '../utils/imageUtils';

import { SampleTagResponse } from '../types';

export function BlockSamplePDF({ tagData }: { tagData: SampleTagResponse }) {
  const sampleType = tagData?.type_of_sample_tag;
  const developmentType = tagData?.type_of_development;

  const getOtherAttributes = () => {
    const validAttributes = [
      tagData?.pants_attribute,
      tagData?.tops_attribute,
    ].filter((attr) => attr && attr !== 'Not Applicable');
    return validAttributes.length > 0 ? validAttributes.join(', ') : '-';
  };

  const productImageUrl = getImageUrlForPDF(
    tagData?.product_image,
    tagData?.productImageDataUrl,
  );

  // Derived values
  const borderColor = getBorderColor(sampleType, developmentType);
  const containerStyle = {
    ...styles.container,
    border: `15px solid ${borderColor}`,
  };
  const barcodeValue = tagData?.sample_tag_lpn || tagData?.id;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={containerStyle}>
          {/* Main Content: Two Column Layout */}
          <View style={styles.mainContent}>
            {/* Left Column - All Content */}
            <View style={styles.leftColumn}>
              {/* Logo and Date Header */}
              <View style={styles.logoDateHeader}>
                <PDFLogo division="Men's" />
                <Text
                  style={{
                    fontSize: 7,
                    marginLeft: 10,
                    alignSelf: 'flex-end',
                    fontWeight: 'medium',
                  }}
                >
                  Date: {formatDateUTC(tagData?.date_sent) || '-'}
                </Text>
              </View>

              {/* Horizontal Line */}
              <View style={[styles.horizontalLine, { marginBottom: 2 }]} />

              {/* Sample Type Header */}
              <Text style={styles.sampleTypeHeader}>BLOCK SAMPLE</Text>

              {/* Product Details */}
              <InfoRow
                label="Vendor"
                value={
                  tagData?.company_name || tagData?.vendor_legal_name || '-'
                }
                compact
              />
              <InfoRow
                label="Block Number"
                value={tagData?.block_number || '-'}
                compact
              />
              <InfoRow
                label="Category"
                value={tagData?.category || '-'}
                compact
              />
              <InfoRow
                label="Other Attributes"
                value={getOtherAttributes()}
                compact
              />
              <InfoRow
                label="Cut Date"
                value={
                  tagData?.cut_date ? formatDateUTC(tagData?.cut_date) : '-'
                }
                compact
              />
              <WrappingInfoRow
                label="Fabric Code & Name"
                value={tagData?.self_shell_fabric_code || '-'}
                compact
              />

              {/* Sample Made With Table - Only Fabric */}
              <View style={{ marginTop: 10 }}>
                {/* Table Header */}
                <View style={styles.tableRow}>
                  <CompactTableCell
                    width="40%"
                    textStyle={{ fontSize: 5.5, fontWeight: 'semibold' }}
                    padding={3}
                  >
                    Sample Made With:
                  </CompactTableCell>
                  <CompactTableCell
                    width="30%"
                    textAlign="center"
                    textStyle={{ fontSize: 5.5 }}
                    padding={3}
                  >
                    Available
                  </CompactTableCell>
                  <CompactTableCell
                    width="30%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={{ fontSize: 5.5 }}
                    padding={3}
                  >
                    Production
                  </CompactTableCell>
                </View>

                {/* Fabric Row Only */}
                <TableRow isLast>
                  <CompactTableCell
                    width="40%"
                    textStyle={{ fontSize: 7 }}
                    padding={3}
                  >
                    Fabric
                  </CompactTableCell>
                  <CheckMarkCell
                    hasCheckMark={
                      tagData?.sample_created_with?.fabric === 'Available'
                    }
                    hasBorder
                  />
                  <CheckMarkCell
                    hasCheckMark={
                      tagData?.sample_created_with?.fabric === 'Production'
                    }
                    hasBorder={false}
                  />
                </TableRow>
              </View>
            </View>

            {/* Right Column - Photo and Barcode */}
            <View style={styles.rightColumn}>
              <ProductImage imageUrl={productImageUrl} />
              <PDFBarcode value={barcodeValue} />
            </View>
          </View>

          {/* Comments Section */}
          <View style={styles.commentsSection}>
            <View style={styles.commentsLeft}>
              <Text
                style={{
                  fontSize: 7,
                  fontWeight: 'semibold',
                  marginBottom: 3,
                }}
              >
                Vendor Comments:
              </Text>
              <View style={{ height: 60 }}>
                <Text style={{ fontSize: 6, padding: 1 }}>
                  {tagData?.comments}
                </Text>
              </View>
            </View>

            {/* Vertical divider line */}
            <View style={styles.verticalDivider} />

            <View style={styles.commentsRight}>
              <Text style={styles.internalHeader}>FN INTERNAL ONLY</Text>
              <Text
                style={{
                  fontSize: 7,
                  fontWeight: 'medium',
                  marginBottom: 45,
                }}
              >
                Comments:
              </Text>

              {/* Acceptance Section */}
              <View style={{ marginTop: 20, marginBottom: 15 }}>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: 5,
                  }}
                >
                  <View
                    style={{
                      width: 12,
                      height: 12,
                      border: '1px solid #000',
                      marginRight: 5,
                    }}
                  />
                  <Text style={{ fontSize: 7, marginRight: 15 }}>Accepted</Text>
                  <View
                    style={{
                      width: 12,
                      height: 12,
                      border: '1px solid #000',
                      marginRight: 5,
                    }}
                  />
                  <Text style={{ fontSize: 7 }}>Rejected</Text>
                </View>
                {/* Date Accepted/Rejected */}
                <View
                  style={{
                    borderBottomWidth: 0.5,
                    borderBottomColor: '#000',
                    marginBottom: 2,
                    width: '100%',
                    marginTop: 10,
                  }}
                />
                <Text style={{ fontSize: 7, fontWeight: 'medium' }}>
                  Date Accepted/Rejected
                </Text>
              </View>

              {/* Date Received */}
              <View
                style={{
                  borderBottomWidth: 0.5,
                  borderBottomColor: '#000',
                  marginBottom: 2,
                  width: '100%',
                  marginTop: 10,
                }}
              />
              <Text style={{ fontSize: 7, fontWeight: 'medium' }}>
                Date Received:
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}
